import { apiClient } from '@/config/api';

export interface Session {
  sessionId: string;
  userId: string;
  userEmail: string;
  isAdmin: boolean;
  lastActivity: Date;
  expiresAt: Date;
}

export interface CreateSessionResponse {
  success: boolean;
  sessionId: string;
  message: string;
}

export interface ValidateSessionResponse {
  success: boolean;
  session: Session;
}

class SessionService {
  private sessionId: string | null = null;

  // Get session ID from localStorage
  getSessionId(): string | null {
    if (!this.sessionId) {
      this.sessionId = localStorage.getItem('sessionId');
    }
    return this.sessionId;
  }

  // Set session ID
  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
    localStorage.setItem('sessionId', sessionId);
  }

  // Clear session ID
  clearSessionId() {
    this.sessionId = null;
    localStorage.removeItem('sessionId');
  }

  // Create new session
  async createSession(userId: string, userEmail: string, isAdmin: boolean = false): Promise<string> {
    try {
      const response = await apiClient.post('/session/create', {
        userId,
        userEmail,
        isAdmin
      });

      if (response.data.success) {
        this.setSessionId(response.data.sessionId);
        return response.data.sessionId;
      } else {
        throw new Error('Failed to create session');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create session');
    }
  }

  // Validate current session
  async validateSession(): Promise<Session | null> {
    const sessionId = this.getSessionId();
    
    if (!sessionId) {
      return null;
    }

    try {
      const response = await apiClient.get(`/session/validate/${sessionId}`);
      
      if (response.data.success) {
        return response.data.session;
      } else {
        this.clearSessionId();
        return null;
      }
    } catch (error: any) {
      console.error('Session validation failed:', error);
      this.clearSessionId();
      return null;
    }
  }

  // Invalidate current session
  async invalidateSession(): Promise<boolean> {
    const sessionId = this.getSessionId();
    
    if (!sessionId) {
      return true;
    }

    try {
      const response = await apiClient.delete(`/session/invalidate/${sessionId}`);
      
      if (response.data.success) {
        this.clearSessionId();
        return true;
      } else {
        return false;
      }
    } catch (error: any) {
      console.error('Session invalidation failed:', error);
      // Clear session anyway
      this.clearSessionId();
      return false;
    }
  }

  // Check if session is valid
  async isSessionValid(): Promise<boolean> {
    const session = await this.validateSession();
    return session !== null;
  }

  // Get session info without validation
  getSessionInfo(): { sessionId: string | null; hasSession: boolean } {
    const sessionId = this.getSessionId();
    return {
      sessionId,
      hasSession: !!sessionId
    };
  }

  // Refresh session (extend expiry)
  async refreshSession(): Promise<boolean> {
    const session = await this.validateSession();
    return session !== null;
  }
}

export const sessionService = new SessionService();
